import React, { useState } from "react";
import { Plus, Trash2, ChevronRight, ChevronLeft, Check, Zap, Database, Layers, Globe, Code2, Cloud, Package, Settings, Server } from "lucide-react";

// ─── Constantes ───────────────────────────────────────────────────────────────
const STEP_ICONS = [Package, Database, Layers, Cloud];
const STEP_LABELS = ["Proyecto", "Modelo", "Servicio", "Deploy"];
const STEP_SUBLABELS = ["Runtime + DB", "Entidades + campos", "Service + anotaciones", "BTP + mta.yaml"];

const CAP_TYPES = [
  { cds: "String", js: "string", desc: "Texto" },
  { cds: "Integer", js: "number", desc: "Entero" },
  { cds: "Decimal", js: "number", desc: "Decimal" },
  { cds: "Boolean", js: "boolean", desc: "Booleano" },
  { cds: "Date", js: "string", desc: "Fecha" },
  { cds: "DateTime", js: "string", desc: "Fecha/Hora" },
  { cds: "UUID", js: "string", desc: "UUID" },
  { cds: "LargeString", js: "string", desc: "Texto largo" },
];

const CapTypeSelect = ({ value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  if (disabled) return (
    <div className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-[11px] text-blue-400 font-mono opacity-40 whitespace-nowrap">
      {value}
    </div>
  );
  return (
    <div className="relative" style={{ zIndex: 999 }}>
      <button
        onClick={() => setOpen(!open)}
        className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-[11px] text-emerald-400 font-mono flex items-center gap-1.5 hover:border-emerald-500/40 transition-all whitespace-nowrap"
      >
        {value}
        <ChevronRight size={10} className={`transition-transform shrink-0 ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-[#0B0F1A] border border-white/15 rounded-xl z-[9999] shadow-2xl min-w-[160px]" style={{ position: "absolute", zIndex: 9999 }}>
          <div className="max-h-48 overflow-y-auto">
            {CAP_TYPES.map(t => (
              <button
                key={t.cds}
                onClick={() => { onChange(t.cds); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-[11px] font-mono hover:bg-emerald-600/20 transition-colors ${value === t.cds ? "text-emerald-400 bg-emerald-600/10" : "text-white/60"}`}
              >
                {t.cds}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const RUNTIMES = [
  { id: "nodejs", label: "Node.js", icon: "🟢", desc: "JavaScript/TypeScript. Recomendado para proyectos nuevos." },
  { id: "java", label: "Java", icon: "☕", desc: "Spring Boot. Para empresas con stack Java existente." },
];

const DATABASES = [
  { id: "hana", label: "SAP HANA", icon: "🔵", desc: "Producción BTP. Alto rendimiento." },
  { id: "sqlite", label: "SQLite", icon: "📦", desc: "Desarrollo local. Sin configuración." },
  { id: "pg", label: "PostgreSQL", icon: "🐘", desc: "Alternativa open source." },
];

const initialCapState = {
  // Paso 1
  projectName: "",
  runtime: "nodejs",
  database: "sqlite",
  namespace: "",
  // Paso 2
  entities: [
    {
      name: "",
      fields: [
        { name: "ID", type: "UUID", isKey: true, notNull: true },
        { name: "createdAt", type: "DateTime", isKey: false, notNull: false },
        { name: "", type: "String", isKey: false, notNull: false },
      ],
    },
  ],
  associations: [],
  // Paso 3
  serviceName: "",
  serviceEntities: [],
  fioriAnnotations: true,
  oDataVersion: "v4",
  authEnabled: true,
  // Paso 4
  mtaEnabled: true,
  appRouterEnabled: true,
  xsuaaEnabled: true,
  destinationEnabled: false,
  hdiEnabled: false,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const Badge = ({ children, color = "emerald" }) => {
  const colors = {
    emerald: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    blue: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    amber: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    violet: "bg-violet-500/15 text-violet-400 border-violet-500/30",
    cyan: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
  };
  return (
    <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${colors[color]}`}>
      {children}
    </span>
  );
};

const Label = ({ children }) => (
  <p className="text-[10px] font-black text-white/40 uppercase tracking-[0.15em] mb-2">{children}</p>
);

const Input = ({ value, onChange, placeholder, className = "" }) => (
  <input
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    className={`w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-[12px] text-white font-mono placeholder:text-white/20 outline-none focus:border-emerald-500/50 transition-all ${className}`}
  />
);

const Toggle = ({ checked, onChange, label, sublabel }) => (
  <div className="flex items-center justify-between">
    <div>
      <span className="text-[12px] text-white/60 font-bold block">{label}</span>
      {sublabel && <span className="text-[10px] text-white/25">{sublabel}</span>}
    </div>
    <button
      onClick={() => onChange(!checked)}
      className={`w-12 h-6 rounded-full transition-all duration-300 relative shrink-0 ml-4 ${checked ? "bg-emerald-600" : "bg-white/10"}`}
    >
      <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all duration-300 ${checked ? "left-7" : "left-1"}`} />
    </button>
  </div>
);

// ─── Paso 1: Proyecto ─────────────────────────────────────────────────────────
const Step1 = ({ data, setData }) => {
  const handleProjectName = (val) => {
    const clean = val.replace(/[^a-zA-Z0-9-_]/g, "").toLowerCase();
    setData(prev => ({
      ...prev,
      projectName: clean,
      namespace: clean ? `com.${clean}` : "",
      serviceName: clean ? `${clean.charAt(0).toUpperCase() + clean.slice(1)}Service` : "",
    }));
  };

  return (
    <div className="space-y-6">
      {/* Runtime */}
      <div>
        <Label>Runtime</Label>
        <div className="grid grid-cols-2 gap-3">
          {RUNTIMES.map(r => (
            <button
              key={r.id}
              onClick={() => setData(prev => ({ ...prev, runtime: r.id }))}
              className={`p-4 rounded-2xl border text-left transition-all ${data.runtime === r.id
                ? "bg-emerald-600/20 border-emerald-500/60 shadow-lg shadow-emerald-500/10"
                : "bg-black/20 border-white/8 hover:border-white/20"}`}
            >
              <span className="text-2xl block mb-2">{r.icon}</span>
              <p className={`text-[11px] font-black uppercase mb-1 ${data.runtime === r.id ? "text-emerald-400" : "text-white"}`}>
                {r.label}
              </p>
              <p className="text-[10px] text-white/30 leading-tight">{r.desc}</p>
              {data.runtime === r.id && (
                <div className="mt-2 text-[8px] font-black uppercase text-emerald-500 tracking-widest">● ACTIVO</div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Base de datos */}
      <div>
        <Label>Base de Datos</Label>
        <div className="grid grid-cols-3 gap-3">
          {DATABASES.map(db => (
            <button
              key={db.id}
              onClick={() => setData(prev => ({ ...prev, database: db.id }))}
              className={`p-3 rounded-2xl border text-left transition-all ${data.database === db.id
                ? "bg-emerald-600/15 border-emerald-500/50"
                : "bg-black/20 border-white/8 hover:border-white/20"}`}
            >
              <span className="text-xl block mb-1.5">{db.icon}</span>
              <p className={`text-[10px] font-black uppercase ${data.database === db.id ? "text-emerald-400" : "text-white/60"}`}>
                {db.label}
              </p>
              <p className="text-[9px] text-white/25 mt-0.5 leading-tight">{db.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Nombre + namespace */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Nombre del Proyecto</Label>
          <Input
            value={data.projectName}
            onChange={e => handleProjectName(e.target.value)}
            placeholder="my-cap-app"
          />
          <p className="text-[10px] text-white/20 mt-1 font-mono">npm: {data.projectName || "my-cap-app"}</p>
        </div>
        <div>
          <Label>Namespace CDS</Label>
          <Input
            value={data.namespace}
            onChange={e => setData(prev => ({ ...prev, namespace: e.target.value.toLowerCase() }))}
            placeholder="com.mycompany"
          />
        </div>
      </div>
    </div>
  );
};

// ─── Paso 2: Modelo ───────────────────────────────────────────────────────────
const Step2 = ({ data, setData }) => {
  const addEntity = () => {
    setData(prev => ({
      ...prev,
      entities: [...prev.entities, {
        name: "",
        fields: [
          { name: "ID", type: "UUID", isKey: true, notNull: true },
          { name: "", type: "String", isKey: false, notNull: false },
        ],
      }],
    }));
  };

  const updateEntity = (eIdx, key, value) => {
    setData(prev => ({
      ...prev,
      entities: prev.entities.map((e, i) => i === eIdx ? { ...e, [key]: value } : e),
    }));
  };

  const addField = (eIdx) => {
    setData(prev => ({
      ...prev,
      entities: prev.entities.map((e, i) => i === eIdx
        ? { ...e, fields: [...e.fields, { name: "", type: "String", isKey: false, notNull: false }] }
        : e),
    }));
  };

  const updateField = (eIdx, fIdx, key, value) => {
    setData(prev => ({
      ...prev,
      entities: prev.entities.map((e, i) => i === eIdx
        ? { ...e, fields: e.fields.map((f, j) => j === fIdx ? { ...f, [key]: value } : f) }
        : e),
    }));
  };

  const removeField = (eIdx, fIdx) => {
    setData(prev => ({
      ...prev,
      entities: prev.entities.map((e, i) => i === eIdx
        ? { ...e, fields: e.fields.filter((_, j) => j !== fIdx) }
        : e),
    }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Entidades del Modelo</Label>
        <button
          onClick={addEntity}
          className="flex items-center gap-1.5 text-[10px] font-black text-emerald-400 hover:text-emerald-300 uppercase tracking-widest"
        >
          <Plus size={12} /> Nueva Entidad
        </button>
      </div>

      {data.entities.map((entity, eIdx) => (
        <div key={eIdx} className="bg-black/30 border border-white/8 rounded-2xl">
          {/* Entity header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-emerald-600/10 border-b border-white/5">
            <div className="w-6 h-6 rounded-lg bg-emerald-600/30 border border-emerald-500/30 flex items-center justify-center">
              <Database size={12} className="text-emerald-400" />
            </div>
            <Input
              value={entity.name}
              onChange={e => updateEntity(eIdx, "name", e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1))}
              placeholder="Orders"
              className="flex-1 bg-transparent border-none px-0 text-emerald-300 font-black"
            />
            <Badge color="emerald">entity</Badge>
            {eIdx > 0 && (
              <button
                onClick={() => setData(prev => ({ ...prev, entities: prev.entities.filter((_, i) => i !== eIdx) }))}
                className="text-white/20 hover:text-rose-400 transition-colors"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>

          {/* Fields */}
          <div className="p-4 space-y-2">
            {entity.fields.map((field, fIdx) => (
              <div key={fIdx} className="flex gap-2 items-center">
                <div className={`w-2 h-2 rounded-full shrink-0 ${field.isKey ? "bg-amber-400" : "bg-emerald-400/50"}`} />
                <input
                  value={field.name}
                  onChange={e => updateField(eIdx, fIdx, "name", e.target.value)}
                  placeholder="fieldName"
                  disabled={field.isKey && field.name === "ID"}
                  className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-[12px] text-white font-mono placeholder:text-white/20 outline-none focus:border-emerald-500/50 disabled:opacity-40 disabled:cursor-not-allowed"
                />
                <CapTypeSelect
                  value={field.type}
                  onChange={val => updateField(eIdx, fIdx, "type", val)}
                  disabled={field.isKey && field.name === "ID"}
                />
                <button
                  onClick={() => updateField(eIdx, fIdx, "notNull", !field.notNull)}
                  className={`text-[9px] font-black px-2 py-1 rounded-lg border transition-all ${field.notNull ? "bg-rose-500/15 text-rose-400 border-rose-500/30" : "bg-white/5 text-white/20 border-white/10"}`}
                >
                  !null
                </button>
                {!(field.isKey && field.name === "ID") && (
                  <button onClick={() => removeField(eIdx, fIdx)} className="text-white/20 hover:text-rose-400 shrink-0">
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={() => addField(eIdx)}
              className="flex items-center gap-1.5 text-[10px] font-black text-white/30 hover:text-emerald-400 uppercase tracking-widest transition-colors mt-2"
            >
              <Plus size={11} /> Campo
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Paso 3: Servicio ─────────────────────────────────────────────────────────
const Step3 = ({ data, setData }) => (
  <div className="space-y-6">
    {/* Nombre del servicio */}
    <div>
      <Label>Nombre del Servicio CDS</Label>
      <Input
        value={data.serviceName}
        onChange={e => setData(prev => ({ ...prev, serviceName: e.target.value }))}
        placeholder="CatalogService"
      />
      <p className="text-[10px] text-white/20 mt-1 font-mono">→ /odata/{data.oDataVersion}/{data.serviceName || "CatalogService"}</p>
    </div>

    {/* OData Version */}
    <div>
      <Label>Versión OData</Label>
      <div className="grid grid-cols-2 gap-3">
        {["v4", "v2"].map(v => (
          <button
            key={v}
            onClick={() => setData(prev => ({ ...prev, oDataVersion: v }))}
            className={`p-3 rounded-xl border text-left transition-all flex items-center gap-3 ${data.oDataVersion === v
              ? "bg-emerald-600/15 border-emerald-500/40"
              : "bg-black/20 border-white/8 hover:border-white/20"}`}
          >
            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${data.oDataVersion === v ? "border-emerald-500 bg-emerald-500" : "border-white/20"}`}>
              {data.oDataVersion === v && <div className="w-2 h-2 rounded-full bg-white" />}
            </div>
            <div>
              <p className={`text-[11px] font-black uppercase ${data.oDataVersion === v ? "text-emerald-400" : "text-white/40"}`}>
                {v.toUpperCase()}
              </p>
              <p className="text-[9px] text-white/20">{v === "v4" ? "Recomendado" : "Legacy"}</p>
            </div>
          </button>
        ))}
      </div>
    </div>

    {/* Entidades expuestas */}
    <div>
      <Label>Entidades Expuestas en el Servicio</Label>
      {data.entities.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-xl p-6 text-center">
          <p className="text-[11px] text-white/20">Definí entidades en el paso anterior</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.entities.map((entity, i) => (
            <div key={i} className="flex items-center justify-between bg-black/30 border border-white/8 rounded-xl px-4 py-3">
              <div className="flex items-center gap-3">
                <Database size={14} className="text-emerald-400" />
                <span className="text-[12px] font-black text-white">{entity.name || `Entity${i + 1}`}</span>
              </div>
              <Badge color="emerald">expuesta</Badge>
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Toggles */}
    <div className="space-y-3 bg-white/3 border border-white/8 rounded-2xl p-4">
      <Toggle
        checked={data.fioriAnnotations}
        onChange={val => setData(prev => ({ ...prev, fioriAnnotations: val }))}
        label="Anotaciones Fiori Elements"
        sublabel="@UI.lineItem, @UI.selectionField, @UI.headerInfo"
      />
      <div className="h-px bg-white/5" />
      <Toggle
        checked={data.authEnabled}
        onChange={val => setData(prev => ({ ...prev, authEnabled: val }))}
        label="Autenticación XSUAA"
        sublabel="Roles y permisos en xs-security.json"
      />
    </div>
  </div>
);

// ─── Paso 4: Deploy ───────────────────────────────────────────────────────────
const Step4 = ({ data, setData }) => (
  <div className="space-y-6">
    {/* BTP Modules */}
    <div>
      <Label>Módulos BTP a incluir</Label>
      <div className="space-y-3 bg-white/3 border border-white/8 rounded-2xl p-4">
        {[
          { key: "mtaEnabled", label: "mta.yaml", sublabel: "Descriptor de deploy multitarget en BTP" },
          { key: "appRouterEnabled", label: "App Router", sublabel: "@sap/approuter — enrutamiento y autenticación" },
          { key: "xsuaaEnabled", label: "XSUAA", sublabel: "xs-security.json — servicio de autenticación SAP" },
          { key: "destinationEnabled", label: "Destination Service", sublabel: "Conexión a sistemas on-premise" },
          { key: "hdiEnabled", label: "HDI Container (HANA)", sublabel: "Schema de base de datos en HANA Cloud" },
        ].map(item => (
          <React.Fragment key={item.key}>
            <Toggle
              checked={data[item.key]}
              onChange={val => setData(prev => ({ ...prev, [item.key]: val }))}
              label={item.label}
              sublabel={item.sublabel}
            />
            <div className="h-px bg-white/5 last:hidden" />
          </React.Fragment>
        ))}
      </div>
    </div>

    {/* Preview estructura */}
    <div>
      <Label>Estructura del Proyecto</Label>
      <div className="bg-black/40 border border-white/8 rounded-2xl p-4 font-mono text-[11px] space-y-1">
        {[
          { indent: 0, text: `📁 ${data.projectName || "my-cap-app"}/`, color: "text-white" },
          { indent: 1, text: "📁 db/", color: "text-emerald-400" },
          { indent: 2, text: "schema.cds", color: "text-white/60" },
          { indent: 1, text: "📁 srv/", color: "text-blue-400" },
          { indent: 2, text: "service.cds", color: "text-white/60" },
          { indent: 2, text: `${data.runtime === "nodejs" ? "service.js" : "ServiceHandler.java"}`, color: "text-white/60" },
          { indent: 1, text: "📁 app/", color: "text-violet-400" },
          { indent: 2, text: "fiori-app/", color: "text-white/60" },
          { indent: 1, text: "package.json", color: "text-amber-400" },
          data.mtaEnabled && { indent: 1, text: "mta.yaml", color: "text-amber-400" },
          data.xsuaaEnabled && { indent: 1, text: "xs-security.json", color: "text-rose-400" },
          data.appRouterEnabled && { indent: 1, text: "xs-app.json", color: "text-rose-400" },
        ].filter(Boolean).map((item, i) => (
          <div key={i} style={{ paddingLeft: `${item.indent * 16}px` }} className={item.color}>
            {item.text}
          </div>
        ))}
      </div>
    </div>

    {/* Resumen */}
    <div className="bg-gradient-to-br from-emerald-600/10 to-cyan-600/10 border border-emerald-500/20 rounded-2xl p-5">
      <p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mb-4 flex items-center gap-2">
        <Cloud size={12} /> Resumen — Stack CAP a generar
      </p>
      <div className="grid grid-cols-2 gap-2">
        {[
          { label: "Runtime", value: data.runtime === "nodejs" ? "Node.js" : "Java" },
          { label: "Base de datos", value: data.database === "hana" ? "SAP HANA" : data.database === "sqlite" ? "SQLite" : "PostgreSQL" },
          { label: "OData", value: data.oDataVersion.toUpperCase() },
          { label: "Entidades", value: `${data.entities.length} definidas` },
          { label: "Fiori", value: data.fioriAnnotations ? "✅ Incluido" : "❌ No" },
          { label: "XSUAA", value: data.xsuaaEnabled ? "✅ Incluido" : "❌ No" },
        ].map((item, i) => (
          <div key={i} className="flex items-center justify-between">
            <span className="text-[11px] text-white/40">{item.label}</span>
            <span className="text-[11px] font-black text-emerald-400">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ─── Componente Principal ─────────────────────────────────────────────────────
const CapWizard = ({ data, setData }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [capData, setCapData] = useState(initialCapState);

  const isInitialized = React.useRef(false);

  // Cargar variante
  React.useEffect(() => {
    if (isInitialized.current) return;
    if (data?.entities) {
      setCapData(prev => ({ ...prev, ...data }));
      isInitialized.current = true;
    }
  }, [data]);

  // Reset cuando se limpia todo
  React.useEffect(() => {
    if (data?._capReset) {
      setCapData(initialCapState);
      isInitialized.current = false;
    }
  }, [data?._capReset]);

  const updateCap = (key, value) => {
    setCapData(prev => ({ ...prev, [key]: value }));
    setData(prev => ({ ...prev, [key]: value }));
  };

  // Merge con initialCapState si data viene vacío
  const mergedData = { ...initialCapState, ...data };
  const mergedSetData = setData;

  const steps = [
    <Step1 key={0} data={capData} setData={setCapData} />,
    <Step2 key={1} data={capData} setData={setCapData} />,
    <Step3 key={2} data={capData} setData={setCapData} />,
    <Step4 key={3} data={capData} setData={setCapData} />,
  ];

  return (
    <div className="bg-white/3 border border-white/8 rounded-[28px] overflow-visible">
      {/* Header BAS Style */}
      <div className="bg-gradient-to-r from-emerald-600/15 to-cyan-600/10 border-b border-white/8 p-5">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-8 h-8 rounded-xl bg-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
            <Cloud size={16} className="text-white" />
          </div>
          <div>
            <p className="text-[13px] font-black text-white uppercase tracking-wide leading-none">CAP Architect</p>
            <p className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest mt-0.5">
              Cloud Application Programming · SAP BTP
            </p>
          </div>
          <div className="ml-auto flex gap-2">
            <Badge color="emerald">BTP Premium</Badge>
            <Badge color="cyan">Node.js / Java</Badge>
          </div>
        </div>

        {/* Step Indicator — estilo BAS */}
        <div className="flex items-center gap-0">
          {STEP_LABELS.map((label, i) => {
            const Icon = STEP_ICONS[i];
            const isActive = i === currentStep;
            const isDone = i < currentStep;
            return (
              <React.Fragment key={i}>
                <button
                  onClick={() => setCurrentStep(i)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all ${isActive
                    ? "bg-emerald-600 shadow-lg shadow-emerald-500/30"
                    : isDone
                      ? "bg-emerald-600/20 hover:bg-emerald-600/30"
                      : "bg-white/5 hover:bg-white/10"}`}
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${isActive ? "bg-white/20" : isDone ? "bg-emerald-500/40" : "bg-white/10"}`}>
                    {isDone ? <Check size={10} className="text-emerald-400" /> : <Icon size={10} className={isActive ? "text-white" : "text-white/40"} />}
                  </div>
                  <div className="text-left hidden sm:block">
                    <p className={`text-[10px] font-black uppercase leading-none ${isActive ? "text-white" : isDone ? "text-emerald-400" : "text-white/30"}`}>
                      {label}
                    </p>
                    <p className={`text-[9px] leading-none mt-0.5 ${isActive ? "text-white/60" : "text-white/20"}`}>
                      {STEP_SUBLABELS[i]}
                    </p>
                  </div>
                </button>
                {i < STEP_LABELS.length - 1 && (
                  <ChevronRight size={14} className={`shrink-0 mx-1 ${i < currentStep ? "text-emerald-400" : "text-white/15"}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="p-6">
        {steps[currentStep]}
      </div>

      {/* Navigation */}
      <div className="border-t border-white/8 px-6 py-4 flex items-center justify-between bg-black/20">
        <button
          onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))}
          disabled={currentStep === 0}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] font-black uppercase tracking-widest text-white/40 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed transition-all"
        >
          <ChevronLeft size={14} /> Anterior
        </button>

        <div className="flex gap-1.5">
          {STEP_LABELS.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentStep(i)}
              className={`h-2 rounded-full transition-all ${i === currentStep ? "bg-emerald-500 w-6" : i < currentStep ? "bg-emerald-500/40 w-2" : "bg-white/15 w-2"}`}
            />
          ))}
        </div>

        <button
          onClick={() => setCurrentStep(prev => Math.min(STEP_LABELS.length - 1, prev + 1))}
          disabled={currentStep === STEP_LABELS.length - 1}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] font-black uppercase tracking-widest bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-20 disabled:cursor-not-allowed transition-all shadow-lg shadow-emerald-500/20"
        >
          Siguiente <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
};

export default CapWizard;